import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Button, Card, CardBody, CardHeader, FormGroup, Input, Label } from 'reactstrap'

const SettlementEmailDeliveryReconciliation = ({ settlement, busy = false, onConfirm }) => {
  const [ paymentId, setPaymentId ] = useState('')
  const [ status, setStatus ] = useState('delivered')
  const [ note, setNote ] = useState('')
  const [ confirmed, setConfirmed ] = useState(false)
  const [ localBusy, setLocalBusy ] = useState(false)
  const [ error, setError ] = useState('')
  const submitting = useRef(false)
  const allowed = settlement.settlement_kind === 'consignor' && settlement.permissions?.can_mark_email_delivered === true
  const candidates = (settlement.payments || []).filter((payment) => payment.can_mark_email_delivered === true)
  const selected = candidates.find((payment) => String(payment.id) === paymentId)
  const selectedVersion = selected?.email_reconciliation_token || ''
  const candidateIds = candidates.map((payment) => String(payment.id)).join(',')
  useEffect(() => {
    if (!candidateIds.split(',').includes(paymentId)) setPaymentId(candidateIds.split(',').filter(Boolean).length === 1 ? candidateIds : '')
  }, [ candidateIds, paymentId ])
  useEffect(() => { setConfirmed(false); setNote(''); setError('') }, [ paymentId, selectedVersion, settlement.email_delivery_reconciliation_token ])

  if (!allowed || !candidates.length) return null
  const submit = async () => {
    if (submitting.current || busy || !selected || !confirmed || !note.trim()) return
    submitting.current = true
    setLocalBusy(true)
    setError('')
    try {
      const success = await onConfirm(selected.id, {
        confirmed: true, status, note: note.trim(),
        expected_email_attempt: selected.email_reconciliation_attempt,
        expected_payment_updated_at: selected.email_reconciliation_token,
        expected_settlement_updated_at: settlement.email_delivery_reconciliation_token
      })
      if (success) {
        setNote('')
        setConfirmed(false)
        window.dispatchEvent(new Event('settlement-email-updated'))
      } else setError('Konfirmasi gagal. Periksa pesan kesalahan di atas, lalu refresh sebelum mencoba lagi.')
    } catch (failure) { setError(failure.message || 'Konfirmasi gagal. Refresh dan periksa status terbaru.') }
    finally { submitting.current = false; setLocalBusy(false) }
  }
  return <Card className='mb-3' aria-label='Manual email delivery confirmation'>
    <CardHeader><strong>Konfirmasi Email Delivered / Failed — Owner/Admin</strong></CardHeader>
    <CardBody>
      <Alert color='info'>Gunakan setelah memeriksa status <strong>Delivered atau Failed (bounce/dropped)</strong> di SendGrid untuk Transfer ID, attempt, dan alamat penerima utama yang sesuai. Ini mengoreksi catatan status tanpa mengirim ulang email atau mengubah pembayaran. Waktu status memakai waktu pembaruan terakhir sebelum koreksi; waktu konfirmasi dan admin dicatat di Settlement Movement.</Alert>
      { error && <Alert color='danger'>{ error }</Alert> }
      <FormGroup>
        <Label for='email-delivery-payment'>Transfer ID yang sudah diperiksa</Label>
        <Input id='email-delivery-payment' type='select' value={ paymentId } disabled={ busy || localBusy } onChange={ (event) => setPaymentId(event.target.value) }>
          <option value=''>Pilih Transfer ID</option>
          { candidates.map((payment) => <option key={ payment.id } value={ String(payment.id) }>{ payment.transfer_id } — Trf { payment.transfer_number } — { payment.email_status }</option>) }
        </Input>
      </FormGroup>
      { selected && <p><strong>Penerima yang diperiksa:</strong> { selected.email_delivery_recipient || selected.recipient_snapshot?.email || '-' }<br /><strong>Email attempt:</strong> { selected.email_reconciliation_attempt }</p> }
      <FormGroup><Label>Status hasil pemeriksaan</Label><Input type='select' value={status} disabled={busy || localBusy} onChange={event => { setStatus(event.target.value); setConfirmed(false) }}><option value='delivered'>Delivered</option><option value='failed'>Failed</option></Input></FormGroup>
      <FormGroup>
        <Label for='email-delivery-note'>Catatan bukti SendGrid <span className='text-danger'>*</span></Label>
        <Input id='email-delivery-note' type='textarea' maxLength={ 2000 } value={ note } disabled={ busy || localBusy } onChange={ (event) => setNote(event.target.value) } placeholder='Contoh: Message ID / tanggal log Delivered, bounce, atau dropped yang sudah diperiksa.' />
      </FormGroup>
      <FormGroup check className='mb-3'>
        <Label check><Input type='checkbox' checked={ confirmed } disabled={ busy || localBusy || !selected } onChange={ (event) => setConfirmed(event.target.checked) } />Saya sudah memeriksa SendGrid: email untuk penerima utama dan attempt ini sesuai status {status} yang dipilih.</Label>
      </FormGroup>
      <Button type='button' color='success' disabled={ busy || localBusy || !selected || !confirmed || !note.trim() } onClick={ submit }>Mark as Email {status}</Button>
      <small className='d-block mt-2 text-muted'>Koreksi berlaku hanya untuk Transfer ID terpilih. Setelah Failed, PC dapat menyimpan email penerima yang sudah terdaftar di User Detail lalu mengirim ulang transfer tersebut. Status delivered pada parent settlement diperbarui setelah seluruh email pembayaran berstatus paid sudah delivered.</small>
    </CardBody>
  </Card>
}

SettlementEmailDeliveryReconciliation.propTypes = { settlement: PropTypes.object.isRequired, busy: PropTypes.bool, onConfirm: PropTypes.func.isRequired }
export default SettlementEmailDeliveryReconciliation
